import { motion } from 'motion/react';
import { useLayoutEffect, useRef, useState } from 'react';

import HeartIcon from '@common/assets/icons/heart.svg?react';
import { CachedImage } from '@common/ui/CachedImage';

import {
  type SuperlikeAnimationPhase,
  type SuperlikeCardSize,
  type SuperlikeLaunchOrigin,
  SUPERLIKE_HEART_SIZE,
  superlikeFxFrame,
  superlikeFxGeometry,
  superlikeWashFrame,
} from './superlikeAnimation';
import styles from './SuperlikeSendAnimation.module.scss';

export type SuperlikeSendAnimationProps = {
  phase: SuperlikeAnimationPhase;
  launchFrom?: SuperlikeLaunchOrigin;
};

const HALF_HEART = SUPERLIKE_HEART_SIZE / 2;

/**
 * The send animation's effects on the card (board 1.1): the heart that
 * flies from the button to the centre, the rings, the 12 small hearts and
 * «Суперлайк». Rendered in the card's overlay slot, so it leaves with the
 * card, or over the card and the feed's button row, whose owner moves it; it
 * takes no taps. The wash under the card's text is SuperlikeWash.
 */
export const SuperlikeSendAnimation = ({
  phase,
  launchFrom,
}: SuperlikeSendAnimationProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [cardSize, setCardSize] = useState<SuperlikeCardSize | null>(null);

  // The card's size before the first paint: the heart starts on the button.
  useLayoutEffect(() => {
    if (!ref.current) return;

    setCardSize({
      width: ref.current.offsetWidth,
      height: ref.current.offsetHeight,
    });
  }, []);

  const geometry = cardSize ? superlikeFxGeometry(cardSize, launchFrom) : null;
  const frame = geometry ? superlikeFxFrame(phase, geometry) : null;

  return (
    <div
      ref={ref}
      className={styles.fx}
      style={{ pointerEvents: 'none' }}
      aria-hidden="true"
      data-test-id="superlike-send-animation"
      data-phase={phase}
    >
      {geometry && frame && (
        <>
          {frame.rings.map((ring, index) => (
            <motion.span
              key={index}
              className={styles.ring}
              data-ring={index}
              style={{
                left: geometry.centreX - HALF_HEART,
                top: geometry.centreY - HALF_HEART,
              }}
              initial={{ scale: 0.45, opacity: 0 }}
              animate={ring.animate}
              transition={ring.transition}
            />
          ))}

          {frame.particles.map((particle, index) => (
            <motion.span
              key={index}
              className={styles.particle}
              data-test-id="superlike-send-animation-particle"
              style={{
                left: geometry.centreX - particle.size / 2,
                top: geometry.centreY - particle.size / 2,
                width: particle.size,
                height: particle.size,
                color: particle.color,
              }}
              initial={{ x: 0, y: 0, scale: 0.3, rotate: 0, opacity: 0 }}
              animate={particle.animate}
              transition={particle.transition}
            >
              <HeartIcon width="100%" height="100%" />
            </motion.span>
          ))}

          <motion.div
            className={styles.heart}
            style={{
              left: geometry.centreX - HALF_HEART,
              top: geometry.centreY - HALF_HEART,
            }}
            initial={phase === 'fadeIn' ? { opacity: 0 } : false}
            animate={frame.heart.animate}
            transition={frame.heart.transition}
          >
            <CachedImage
              testId="superlike-send-animation-heart"
              src="/icons/3d-icons/likes.png"
              alt=""
              blur="0px"
              style={{ width: '100%', height: '100%' }}
            />
          </motion.div>

          <motion.div
            className={styles.label}
            style={{ top: geometry.labelTop }}
            initial={{ opacity: 0, y: 10 }}
            animate={frame.label.animate}
            transition={frame.label.transition}
          >
            Суперлайк
          </motion.div>
        </>
      )}
    </div>
  );
};

export type SuperlikeWashProps = {
  phase: SuperlikeAnimationPhase;
};

/** The pink wash: over the photo, under the card's text (board 1.1). */
export const SuperlikeWash = ({ phase }: SuperlikeWashProps) => {
  const frame = superlikeWashFrame(phase);

  return (
    <motion.div
      className={styles.wash}
      aria-hidden="true"
      data-test-id="superlike-wash"
      initial={{ opacity: 0 }}
      animate={frame.animate}
      transition={frame.transition}
    />
  );
};
